import { getNotaByIdLocal, deleteNotaLocal } from '../db.js';
import { escapeHTML } from '../security.js';
import { getCategoryIcon, DEFAULT_CATEGORIES } from '../categorizer.js';
import { authManager } from '../auth-manager.js';
import { getLinkedListsForNota, openLinkToListModal, unlinkNotaFromShoppingList } from '../utils/reconciliation.js';

export async function renderNotaDetailView(container, id, syncManager) {
  if (!id) {
    window.location.hash = '#notas';
    return;
  }

  let nota = await getNotaByIdLocal(id);

  if (!nota) {
    try {
      const res = await authManager.fetchWithAuth(`/api/notas/${id}`);
      if (res.ok) {
        nota = await res.json();
      }
    } catch (e) {
      console.warn('Backend fetch failed:', e);
    }
  }

  if (!nota) {
    container.innerHTML = `
      <div class="glass-panel text-center" style="padding: 2rem 1rem;">
        <span class="material-symbols-outlined" style="font-size: 3rem; color: var(--color-muted);">receipt_long</span>
        <h2 class="font-bold text-on-surface mt-sm mb-xs">Nota não encontrada</h2>
        <p class="text-muted mb-md">A nota fiscal solicitada não existe ou foi removida.</p>
        <a href="#notas" class="btn btn-primary">Voltar para Minhas Notas</a>
      </div>
    `;
    return;
  }

  const storeName = nota.emitente?.razaoSocial || nota.razaoSocial || 'Detalhe da Nota';
  window.dispatchEvent(new CustomEvent('nfce-page-title-changed', { detail: { title: storeName } }));
  const cnpj = nota.emitente?.cnpj || nota.cnpj || '';
  const endereco = nota.emitente?.endereco || nota.endereco || '';
  const dataStr = nota.dataEmissao ? new Date(nota.dataEmissao).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : 'Data desconhecida';
  const valorTotal = (nota.totais?.valorTotal || nota.valorTotal || 0).toFixed(2).replace('.', ',');
  const tributos = (nota.totais?.tributos || nota.tributos || 0).toFixed(2).replace('.', ',');

  // Group items by category
  const groupedItems = {};
  (nota.itens || []).forEach(item => {
    const catName = item.categoriaNome || 'Outros';
    if (!groupedItems[catName]) {
      groupedItems[catName] = [];
    }
    groupedItems[catName].push(item);
  });

  container.innerHTML = `
    <!-- Header & Back Button -->
    <div class="detail-header">
      <button class="detail-back" id="btn-back">
        <span class="material-symbols-outlined" style="font-size: 18px;">arrow_back</span>
        Voltar
      </button>
      <h2 class="detail-store">${escapeHTML(storeName)}</h2>
      <div class="detail-meta">${escapeHTML(dataStr)}</div>
      ${cnpj ? `<div class="detail-meta">CNPJ: ${escapeHTML(cnpj)}</div>` : ''}
      ${endereco ? `<div class="detail-meta">${escapeHTML(endereco)}</div>` : ''}
    </div>

    <!-- Summary Card -->
    <div class="glass-panel mb-lg" style="padding: 1.25rem;">
      <div class="flex justify-between items-center mb-xs">
        <span class="text-muted" style="font-size: 0.875rem;">Valor Total</span>
        <span class="font-bold text-accent font-mono" style="font-size: 1.75rem;">R$ ${valorTotal}</span>
      </div>
      <div class="flex justify-between items-center text-muted" style="font-size: 0.8125rem;">
        <span>Impostos Incidentes</span>
        <span class="font-mono">R$ ${tributos}</span>
      </div>
    </div>

    <!-- Items Section -->
    <div class="section-header" style="margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center;">
      <h3 class="section-title" style="font-size: 1.25rem;">Itens (${(nota.itens || []).length})</h3>
      <button class="btn btn-secondary" id="btn-smart-categorize" style="font-size: 0.75rem; padding: 0.4rem 0.6rem; display: flex; align-items: center; gap: 0.3rem; background: rgba(99, 102, 241, 0.1); color: #6366F1; border: 1px solid rgba(99,102,241,0.3);">
        <span class="material-symbols-outlined" style="font-size: 16px;">auto_awesome</span> Auto-Categorizar
      </button>
    </div>

    ${Object.keys(groupedItems).length === 0 ? `
      <div class="glass-panel text-center text-muted" style="padding: 1.5rem;">Sem detalhes dos itens.</div>
    ` : Object.entries(groupedItems).map(([catName, items]) => {
    const catTotal = items.reduce((sum, item) => sum + (item.valorTotal || 0), 0);
    const icon = getCategoryIcon(catName);
    return `
        <div class="glass-panel item-group">
          <div class="item-group-header">
            <span style="font-size: 1.25rem; margin-right: 0.5rem;">${icon}</span>
            <span class="item-group-name">${escapeHTML(catName)}</span>
            <span class="item-group-total">R$ ${catTotal.toFixed(2).replace('.', ',')}</span>
          </div>
          ${items.map(item => `
            <div class="item-row cat-editable-item" style="cursor: pointer;" data-item-id="${item.id}" data-item-ean="${item.codigo || item.ean || ''}" data-item-desc="${escapeHTML(item.descricao)}" data-item-cat="${escapeHTML(catName)}">
              <span class="item-desc" title="${escapeHTML(item.descricao)}">${escapeHTML(item.descricao)}</span>
              <span class="item-qty">${escapeHTML(item.quantidade)} ${escapeHTML(item.unidade || 'UN')}</span>
              <span class="item-value">R$ ${(item.valorTotal || 0).toFixed(2).replace('.', ',')}</span>
            </div>
          `).join('')}
        </div>
      `;
  }).join('')}

    <!-- Payment details -->
    ${(() => {
      const validPayments = (nota.pagamentos || []).filter(p => {
        const f = (p.forma || '').toLowerCase();
        return f && !f.includes('pagar') && !f.includes('itens') && !f.includes('tributo') && !f.includes('desconto') && !f.includes('troco') && !f.includes('valor total') && !f.includes('lei federal');
      });
      if (validPayments.length === 0) return '';
      return `
        <div class="glass-panel mb-lg" style="padding: 1rem 1.25rem;">
          <h4 class="font-semibold text-on-surface mb-sm" style="font-size: 1rem;">Forma de Pagamento</h4>
          ${validPayments.map(p => `
            <div class="payment-row">
              <span class="payment-method">${p.forma}</span>
              <span class="payment-value">R$ ${(p.valor || 0).toFixed(2).replace('.', ',')}</span>
            </div>
          `).join('')}
        </div>
      `;
    })()}

    <!-- Linked Shopping Lists Section -->
    <div class="glass-panel mb-lg" id="linked-lists-section" style="padding: 1.25rem;">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 20px; color: #3B82F6;">playlist_add_check</span>
          <h4 class="font-semibold text-on-surface" style="margin: 0; font-size: 1rem;">Listas de Compras Vinculadas</h4>
        </div>
        <button id="btn-open-link-modal-detail" class="btn btn-secondary" style="padding: 0.35rem 0.75rem; font-size: 0.8rem; display: flex; align-items: center; gap: 0.3rem;">
          <span class="material-symbols-outlined" style="font-size: 16px;">link</span> Vincular
        </button>
      </div>

      <div id="linked-lists-container">
        <div style="font-size: 0.82rem; color: var(--text-muted);">Carregando listas vinculadas...</div>
      </div>
    </div>

    <button class="btn btn-danger btn-block mb-lg" id="btn-delete">
      <span class="material-symbols-outlined" style="font-size: 20px;">delete</span>
      Excluir Nota
    </button>

    <!-- Delete Confirmation Modal -->
    <div id="delete-modal" class="modal-overlay hidden" style="display: none !important;">
      <div class="modal-card">
        <div class="modal-icon">
          <span class="material-symbols-outlined" style="font-size: 3rem; color: var(--color-error);">delete_forever</span>
        </div>
        <h3 class="modal-title">Excluir Nota Fiscal?</h3>
        <p class="modal-desc">
          Esta ação removerá permanentemente esta nota e todos os seus itens do sistema.
        </p>
        <div class="flex gap-sm">
          <button class="btn btn-danger" id="btn-confirm-delete" style="flex: 1;">
            Sim, Excluir
          </button>
          <button class="btn btn-secondary" id="btn-cancel-delete" style="flex: 1;">
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Category Modal -->
    <div id="category-modal" class="modal-overlay hidden" style="display: none !important;">
      <div class="modal-card">
        <h3 class="modal-title" style="margin-bottom: 0.5rem; text-align: left;">Alterar Categoria</h3>
        <p class="modal-desc" id="cat-modal-desc" style="font-size: 0.85rem; margin-bottom: 1rem; font-weight: bold; color: var(--text-main); text-align: left;"></p>
        <select id="cat-modal-select" style="width: 100%; box-sizing: border-box; padding: 0.65rem; margin-bottom: 0.5rem; border-radius: 0.5rem; border: 1px solid var(--border-color); background: var(--bg-surface); color: var(--text-main); font-size: 0.95rem; outline: none;">
          ${DEFAULT_CATEGORIES.map(c => `<option value="${c.id}">${c.icone} ${c.nome}</option>`).join('')}
        </select>
        <input type="text" id="cat-modal-custom" placeholder="Digite a nova categoria..." style="width: 100%; box-sizing: border-box; padding: 0.65rem; margin-bottom: 1.2rem; border-radius: 0.5rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); font-size: 0.95rem; outline: none; display: none;">
        <div class="flex gap-sm">
          <button class="btn btn-primary" id="btn-save-category" style="flex: 1;">Salvar p/ Todos</button>
          <button class="btn btn-secondary" id="btn-cancel-category" style="flex: 1;">Cancelar</button>
        </div>
        <p style="font-size: 0.7rem; color: var(--text-muted); margin-top: 0.8rem; text-align: center;">Salvar atualiza o Dicionário Global para este EAN/Nome.</p>
      </div>
    </div>
  `;

  // Linked Shopping Lists management
  const linkedListsContainer = container.querySelector('#linked-lists-container');
  const btnOpenLinkModal = container.querySelector('#btn-open-link-modal-detail');

  async function loadLinkedLists() {
    if (!linkedListsContainer) return;
    try {
      const lists = await getLinkedListsForNota(id);
      if (!lists || lists.length === 0) {
        linkedListsContainer.innerHTML = `
          <div style="font-size: 0.82rem; color: var(--text-muted); display: flex; align-items: center; justify-content: space-between;">
            <span>Nenhuma lista de compras vinculada a este cupom fiscal.</span>
          </div>
        `;
        return;
      }

      linkedListsContainer.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 0.5rem;">
          ${lists.map(l => `
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.65rem 0.85rem; background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.65rem;">
              <a href="#lista/${l.id}" style="text-decoration: none; color: var(--text-main); display: flex; align-items: center; gap: 0.4rem; font-size: 0.875rem; font-weight: 700;">
                <span class="material-symbols-outlined" style="font-size: 17px; color: #10B981;">playlist_add_check</span>
                <span>${escapeHTML(l.name)}</span>
                ${l.scope === 'family' ? `<span style="font-size: 0.65rem; font-weight: 800; background: rgba(99, 102, 241, 0.15); color: #6366F1; padding: 0.05rem 0.35rem; border-radius: var(--radius-pill);">Família</span>` : ''}
              </a>
              <button class="btn-unlink-detail" data-list-id="${l.id}" style="background: none; border: none; color: #EF4444; font-size: 0.75rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 0.2rem; padding: 0.2rem 0.4rem; border-radius: 0.3rem;">
                <span class="material-symbols-outlined" style="font-size: 15px;">link_off</span> Desvincular
              </button>
            </div>
          `).join('')}
        </div>
      `;

      linkedListsContainer.querySelectorAll('.btn-unlink-detail').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const listId = btn.getAttribute('data-list-id');
          btn.disabled = true;
          await unlinkNotaFromShoppingList(listId, id);
          syncManager.showToast('Vínculo removido com sucesso.', 'info');
          await loadLinkedLists();
        });
      });
    } catch (e) {
      linkedListsContainer.innerHTML = `<div style="font-size: 0.82rem; color: #EF4444;">Erro ao carregar listas vinculadas.</div>`;
    }
  }

  loadLinkedLists();

  if (btnOpenLinkModal) {
    btnOpenLinkModal.addEventListener('click', () => {
      openLinkToListModal({
        nota,
        onLinked: async () => {
          syncManager.showToast('Nota vinculada com sucesso à lista!', 'success');
          await loadLinkedLists();
        }
      });
    });
  }

  container.querySelector('#btn-back').addEventListener('click', () => {
    window.location.hash = '#notas';
  });

  const btnDelete = container.querySelector('#btn-delete');
  const deleteModal = container.querySelector('#delete-modal');
  const btnConfirmDelete = container.querySelector('#btn-confirm-delete');
  const btnCancelDelete = container.querySelector('#btn-cancel-delete');

  if (btnDelete && deleteModal) {
    btnDelete.addEventListener('click', (e) => {
      e.preventDefault();
      deleteModal.style.setProperty('display', 'flex', 'important');
      deleteModal.classList.remove('hidden');
    });

    btnCancelDelete?.addEventListener('click', () => {
      deleteModal.style.setProperty('display', 'none', 'important');
      deleteModal.classList.add('hidden');
    });

    btnConfirmDelete?.addEventListener('click', async () => {
      deleteModal.style.setProperty('display', 'none', 'important');
      deleteModal.classList.add('hidden');
      try {
        await deleteNotaLocal(id);
        const res = await authManager.fetchWithAuth(`/api/notas/${id}`, { method: 'DELETE' });
        if (!res.ok) {
          console.warn('Backend delete note returned status:', res.status);
        }
      } catch (err) {
        console.warn('Delete warning:', err);
      }
      syncManager.showToast('Nota excluída com sucesso.', 'info');
      window.location.hash = '#notas';
      window.dispatchEvent(new Event('hashchange'));
    });
  }

  const catModal = container.querySelector('#category-modal');
  const catDesc = container.querySelector('#cat-modal-desc');
  const catSelect = container.querySelector('#cat-modal-select');
  const catCustomInput = container.querySelector('#cat-modal-custom');
  let currentEditItem = null;

  catSelect.addEventListener('change', () => {
    if (catSelect.value === '14') { // 14 is the ID for "Outros"
      catCustomInput.style.display = 'block';
      catCustomInput.classList.remove('hidden');
      catCustomInput.focus();
    } else {
      catCustomInput.style.display = 'none';
      catCustomInput.classList.add('hidden');
    }
  });

  container.querySelectorAll('.cat-editable-item').forEach(row => {
    row.addEventListener('click', () => {
      currentEditItem = {
        id: row.getAttribute('data-item-id'),
        ean: row.getAttribute('data-item-ean'),
        desc: row.getAttribute('data-item-desc'),
        catName: row.getAttribute('data-item-cat')
      };
      catDesc.textContent = currentEditItem.desc;
      
      const foundCat = DEFAULT_CATEGORIES.find(c => c.nome === currentEditItem.catName);
      if (foundCat) {
        catSelect.value = foundCat.id;
      } else {
        catSelect.value = '14'; // Fallback to "Outros"
      }

      if (catSelect.value === '14') {
        catCustomInput.style.display = 'block';
        catCustomInput.classList.remove('hidden');
        if (!foundCat || currentEditItem.catName !== 'Outros') {
          catCustomInput.value = currentEditItem.catName;
        } else {
          catCustomInput.value = '';
        }
      } else {
        catCustomInput.style.display = 'none';
        catCustomInput.classList.add('hidden');
        catCustomInput.value = '';
      }
      
      catModal.style.setProperty('display', 'flex', 'important');
      catModal.classList.remove('hidden');
    });
  });

  container.querySelector('#btn-cancel-category')?.addEventListener('click', () => {
    catModal.style.setProperty('display', 'none', 'important');
    catModal.classList.add('hidden');
  });

  container.querySelector('#btn-save-category')?.addEventListener('click', async () => {
    if (!currentEditItem) return;
    const selectedId = parseInt(catSelect.value, 10);
    const selectedCat = DEFAULT_CATEGORIES.find(c => c.id === selectedId);
    
    let finalCatName = selectedCat.nome;
    if (selectedId === 14 && catCustomInput.value.trim() !== '') {
      finalCatName = catCustomInput.value.trim();
    }
    
    catModal.style.setProperty('display', 'none', 'important');
    catModal.classList.add('hidden');

    const key = currentEditItem.ean ? currentEditItem.ean : currentEditItem.desc;
    
    // Lazy import
    const { saveProductOverride } = await import('../db.js');
    await saveProductOverride({
      key: key,
      ean: currentEditItem.ean || null,
      normalizedName: currentEditItem.desc,
      categoryId: selectedId,
      categoryName: finalCatName,
      updatedAt: Date.now()
    });
    
    syncManager.showToast('Categoria salva no dicionário!', 'success');
    
    // Refresh view
    renderNotaDetailView(container, id, syncManager);
  });

  container.querySelector('#btn-smart-categorize')?.addEventListener('click', async () => {
    syncManager.showToast('Enviando itens para a IA...', 'info');
    
    try {
      const { categorizeWithAI, saveCategoryOverridesBatch } = await import('../ai-categorizer.js');
      
      const itemsToCategorize = (nota.itens || []).filter(i => !i.categoriaNome || i.categoriaNome === 'Outros');
      if (itemsToCategorize.length === 0) {
        syncManager.showToast('Todos os itens já possuem categoria!', 'success');
        return;
      }
      
      const results = await categorizeWithAI(itemsToCategorize);
      await saveCategoryOverridesBatch(results);
      
      syncManager.showToast('Itens auto-categorizados com sucesso!', 'success');
      
      // Refresh view
      renderNotaDetailView(container, id, syncManager);
    } catch(err) {
      console.error(err);
      syncManager.showToast('Erro ao categorizar com IA', 'error');
    }
  });
}
